import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';

import { HelpButton } from '@/components/HelpButton';
import { EmptyState } from '@/components/EmptyState';
import { PageHint } from '@/components/HelpText';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import {
  type Category,
  type CategoryKind,
  createCategory,
  listCategories,
} from '@/lib/finance';

const KIND_LABEL: Record<CategoryKind, string> = {
  income: 'Receita',
  expense: 'Despesa',
  both: 'Ambos',
};

export function CategoriesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const [open, setOpen] = useState(false);

  const tree = useMemo(() => buildTree(data ?? []), [data]);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Categorias</h1>
          <HelpButton topic="categorias" />
        </div>
        <button
          onClick={() => setOpen(true)}
          className="tap rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white"
        >
          + nova
        </button>
      </header>

      <PageHint>
        Categorias agrupam suas transações pra você ver onde gasta. Já vêm várias prontas (com
        badge <em>sistema</em>); crie as suas próprias quando precisar.
      </PageHint>

      {isLoading && <p className="text-slate-500">carregando…</p>}
      {error && <p className="text-red-600">{(error as Error).message}</p>}

      {tree.length === 0 && !isLoading && (
        <EmptyState title="Sem categorias" hint="Algo deu errado — recarregue a página." icon="🏷" />
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {tree.map((root) => (
          <div key={root.id} className="border-b border-slate-100 last:border-b-0">
            <CategoryRow cat={root} indent={0} />
            {root.children.map((c) => (
              <CategoryRow key={c.id} cat={c} indent={1} />
            ))}
          </div>
        ))}
      </div>

      <CreateCategoryModal
        open={open}
        onClose={() => setOpen(false)}
        categories={data ?? []}
        onCreated={() => {
          setOpen(false);
          toast.success('categoria criada');
          qc.invalidateQueries({ queryKey: ['categories'] });
        }}
      />
    </div>
  );
}

interface CategoryNode extends Category {
  children: CategoryNode[];
}

function buildTree(items: Category[]): CategoryNode[] {
  const map = new Map<string, CategoryNode>();
  items.forEach((c) => map.set(c.id, { ...c, children: [] }));
  const roots: CategoryNode[] = [];
  map.forEach((node) => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function CategoryRow({ cat, indent }: { cat: Category; indent: number }) {
  return (
    <div
      className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm"
      style={{ paddingLeft: `${1 + indent * 1.25}rem` }}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span
          className="inline-block h-3 w-3 shrink-0 rounded-full"
          style={{ background: cat.color ?? '#cbd5e1' }}
        />
        <span className="truncate">{cat.name}</span>
        {cat.is_system && (
          <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
            sistema
          </span>
        )}
      </span>
      <span className="shrink-0 text-xs text-slate-500">{KIND_LABEL[cat.kind]}</span>
    </div>
  );
}

function CreateCategoryModal({
  open,
  onClose,
  onCreated,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  categories: Category[];
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CategoryKind>('expense');
  const [parentId, setParentId] = useState('');
  const [color, setColor] = useState('#0ea5e9');
  const [error, setError] = useState<string | null>(null);

  const parentOptions = categories.filter((c) => !c.parent_id);

  const mutation = useMutation({
    mutationFn: createCategory,
    onSuccess: () => {
      setName('');
      setKind('expense');
      setParentId('');
      setColor('#0ea5e9');
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      name,
      kind,
      parent_id: parentId || null,
      color,
    });
  }

  return (
    <Modal open={open} title="Nova categoria" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Nome</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Tipo</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as CategoryKind)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="expense">Despesa</option>
            <option value="income">Receita</option>
            <option value="both">Ambos</option>
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Categoria pai (opcional)</span>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="">— raiz —</option>
            {parentOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Cor</span>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-12 w-24 rounded border border-slate-300"
          />
        </label>
        {error && <p className="text-red-600">{error}</p>}
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'criando…' : 'criar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
